import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { SmsGatewayModel } from '@/types/model/SmsGateway'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

import { TextInput, SelectInput, SwitchInput } from '@/components/atoms'
import { useEnterSubmit } from '@/hooks/ui'

interface SmsGatewayDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    gateway: SmsGatewayModel.Gateway
    onSave: (key: string, body: any) => Promise<void>
    loading: boolean
}

interface FormState {
    credentials: Record<string, string>
    credential_type: 'sandbox' | 'production'
    status: 'active' | 'inactive'
}

export const SmsGatewayDialog = ({
    open,
    onOpenChange,
    gateway,
    onSave,
    loading,
}: SmsGatewayDialogProps) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<FormState>({
        defaultValues: {
            credentials: gateway.credentials || {},
            credential_type: gateway.credential_type || 'sandbox',
            status: gateway.status || 'inactive',
        },
    })

    useEffect(() => {
        if (gateway) {
            reset({
                credentials: gateway.credentials || {},
                credential_type: gateway.credential_type || 'sandbox',
                status: gateway.status || 'inactive',
            })
        }
    }, [gateway, reset])

    const onSubmit = (data: FormState) => {
        onSave(gateway.key, data)
    }

    useEnterSubmit(handleSubmit(onSubmit), open)

    const credentialKeys = Object.keys(gateway.credentials || {})

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent size="sm">
                <DialogHeader>
                    <DialogTitle>Edit {gateway.name}</DialogTitle>
                    <DialogDescription>
                        Update credentials and settings for this SMS gateway.
                    </DialogDescription>
                </DialogHeader>

                <div className="divide-y divide-border-subtle">
                    <section className="space-y-4 pb-4">
                        <h3 className="text-sm font-semibold text-ink">Credentials</h3>
                        {credentialKeys.length === 0 && (
                            <p className="text-sm text-muted-foreground">No credentials required for this gateway.</p>
                        )}
                        {credentialKeys.map((key) => (
                            <TextInput
                                key={key}
                                control={control}
                                name={`credentials.${key}` as any}
                                label={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                                placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                                errors={errors}
                            />
                        ))}
                    </section>

                    <section className="space-y-4 pt-4">
                        <h3 className="text-sm font-semibold text-ink">Settings</h3>
                        <SelectInput
                            control={control}
                            name="credential_type"
                            label="Environment"
                            options={[
                                { label: 'Sandbox', value: 'sandbox' },
                                { label: 'Production', value: 'production' },
                            ]}
                        />

                        <SwitchInput
                            control={control}
                            name="status"
                            label="Status"
                            description="Enable or disable this SMS gateway."
                            activeValue="active"
                            inactiveValue="inactive"
                        />
                    </section>
                </div>

                <DialogFooter>
                    <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSubmit(onSubmit)} disabled={loading}>
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
